import { useEffect, useState, useCallback, useMemo } from 'react'
import { apiGet, apiPatch, apiPost } from '../api'

interface IssueRow {
  id: string
  scope_label: string
  name: string
  description: string | null
  body_md: string
  type: string | null
  priority: string | null
  area: string | null
  effort: string | null
  status: string | null
  rationale: string | null
  priority_override: string | null
  status_override: string | null
  effective_priority: string | null
  effective_status: string | null
  classified_at: number | null
  detected_at: number
  updated_at: number
  closed_at: number | null
}

interface IssuesResponse {
  issues: IssueRow[]
  summary: { p0: number; p1: number; p2: number; p3: number; blocked: number; total: number }
}

const PRIORITY_LABEL: Record<string, string> = {
  p0: 'P0', p1: 'P1', p2: 'P2', p3: 'P3',
}
const PRIORITY_TINT: Record<string, string> = {
  p0: '#ff6b81', p1: '#ffb454', p2: '#9aa6c2', p3: '#6b7795',
}
const STATUS_LABEL: Record<string, string> = {
  open: 'open', blocked: 'blocked', 'in-progress': 'in progress', done: 'done',
}

export default function IssuesPanel() {
  const [data, setData] = useState<IssuesResponse | null>(null)
  const [showDone, setShowDone] = useState(false)
  const [areaFilter, setAreaFilter] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setErr(null)
      const res = await apiGet<IssuesResponse>(`/issues${showDone ? '?show_done=1' : ''}`)
      setData(res)
    } catch (e) {
      setErr(String(e))
    }
  }, [showDone])

  useEffect(() => {
    load()
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
  }, [load])

  const filtered = useMemo(() => {
    if (!data) return []
    if (!areaFilter) return data.issues
    return data.issues.filter(i => i.area === areaFilter)
  }, [data, areaFilter])

  const areas = useMemo(() => {
    if (!data) return []
    const set = new Set(data.issues.map(i => i.area).filter(Boolean) as string[])
    return Array.from(set).sort()
  }, [data])

  async function openInClaude(id: string) {
    try {
      const r = await apiPost<{ ok?: boolean; queue_id?: number; scope_label?: string; error?: string }>(`/issues/${id}/session`, {})
      if (r.ok) {
        alert(`Queued for ${r.scope_label}. The local agent will launch Claude in ~30s.`)
      } else {
        alert(`Queue failed: ${r.error ?? 'unknown'}`)
      }
    } catch (e) {
      alert(`Queue request failed: ${e}`)
    }
  }

  async function setOverride(id: string, field: 'priority_override' | 'status_override', value: string | null) {
    // Optimistic
    setData(prev => {
      if (!prev) return prev
      const next = { ...prev, issues: prev.issues.map(i => i.id === id ? { ...i, [field]: value } : i) }
      return next
    })
    try {
      await apiPatch(`/issues/${id}`, { [field]: value })
    } finally {
      load()
    }
  }

  return (
    <section className="panel issues">
      <div className="panel-header">
        <span>Issues</span>
        <div style={{ display: 'flex', gap: 6, fontSize: '0.75rem', color: 'var(--fg-dim, #9aa6c2)' }}>
          {data && (
            <>
              {(['p0', 'p1', 'p2', 'p3'] as const).map(p => (
                <span key={p} style={{
                  padding: '2px 6px', borderRadius: 4,
                  background: data.summary[p] ? `${PRIORITY_TINT[p]}22` : 'transparent',
                  color: data.summary[p] ? PRIORITY_TINT[p] : 'inherit',
                }}>
                  {PRIORITY_LABEL[p]} {data.summary[p]}
                </span>
              ))}
              {data.summary.blocked > 0 && <span style={{ padding: '2px 6px' }}>blocked {data.summary.blocked}</span>}
            </>
          )}
          <button type="button" onClick={() => setShowDone(v => !v)}
            style={{ minHeight: 28, padding: '0.15rem 0.5rem' }}>
            {showDone ? 'hide done' : 'show done'}
          </button>
        </div>
      </div>

      {areas.length > 1 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setAreaFilter(null)}
            style={{ minHeight: 26, padding: '0.1rem 0.4rem', fontSize: '0.75rem',
              background: !areaFilter ? 'var(--accent, #5fb0ff)22' : 'transparent' }}>all</button>
          {areas.map(a => (
            <button key={a} onClick={() => setAreaFilter(a)}
              style={{ minHeight: 26, padding: '0.1rem 0.4rem', fontSize: '0.75rem',
                background: areaFilter === a ? 'var(--accent, #5fb0ff)22' : 'transparent' }}>{a}</button>
          ))}
        </div>
      )}

      {err && <div className="empty-state">Couldn't load issues: {err}</div>}
      {!data && !err && <div className="empty-state">Loading…</div>}
      {data && filtered.length === 0 && !err && (
        <div className="empty-state">
          {showDone ? 'No issues at all.' : 'No open issues — agent may not have ingested yet.'}
        </div>
      )}

      {data && filtered.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0,
                     display: 'flex', flexDirection: 'column', gap: 3,
                     maxHeight: 320, overflowY: 'auto', paddingRight: 4 }}>
          {filtered.map(i => {
            const ep = i.effective_priority || 'unclassified'
            const tint = PRIORITY_TINT[ep] || '#6b7795'
            const isOpen = expanded === i.id
            return (
              <li key={i.id} style={{
                border: '1px solid rgba(128,160,201,0.10)', borderRadius: 8,
                padding: isOpen ? 8 : '4px 8px',
                background: isOpen ? 'rgba(90,242,255,0.04)' : 'transparent',
                fontSize: '0.78rem',
              }}>
                <button type="button"
                  onClick={() => setExpanded(isOpen ? null : i.id)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    width: '100%', textAlign: 'left', background: 'transparent',
                    border: 'none', padding: 0, cursor: 'pointer', color: 'inherit',
                  }}>
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <span style={{
                      fontSize: '0.7rem', fontWeight: 600,
                      padding: '1px 6px', borderRadius: 3,
                      background: `${tint}22`, color: tint,
                      flexShrink: 0,
                    }}>
                      {(PRIORITY_LABEL[ep] || '?')}{i.priority_override ? '*' : ''}
                    </span>
                    <span style={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {i.name}
                    </span>
                  </span>
                  <span style={{ display: 'flex', gap: 6, fontSize: '0.7rem', color: 'var(--fg-dim, #9aa6c2)', flexShrink: 0 }}>
                    {i.area && <span>{i.area}</span>}
                    {i.effort && <span>·  {i.effort}</span>}
                    <span>· {i.scope_label}</span>
                  </span>
                </button>
                {isOpen && (
                  <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--fg-dim, #9aa6c2)' }}>
                    {i.description && <div style={{ marginBottom: 6 }}>{i.description}</div>}
                    {i.rationale && <div style={{ marginBottom: 6, fontStyle: 'italic' }}>“{i.rationale}”</div>}
                    <details style={{ marginBottom: 8 }}>
                      <summary style={{ cursor: 'pointer' }}>full body</summary>
                      <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', marginTop: 4 }}>
                        {i.body_md}
                      </pre>
                    </details>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.7rem' }}>set priority:</span>
                      {(['p0', 'p1', 'p2', 'p3'] as const).map(p => (
                        <button key={p} onClick={() => setOverride(i.id, 'priority_override', p)}
                          style={{
                            minHeight: 24, padding: '0 6px', fontSize: '0.7rem',
                            background: i.priority_override === p ? `${PRIORITY_TINT[p]}40` : 'transparent',
                            color: PRIORITY_TINT[p],
                          }}>{PRIORITY_LABEL[p]}</button>
                      ))}
                      {i.priority_override && (
                        <button onClick={() => setOverride(i.id, 'priority_override', null)}
                          style={{ minHeight: 24, padding: '0 6px', fontSize: '0.7rem' }}>
                          clear ({i.priority || '?'} from AI)
                        </button>
                      )}
                      <span style={{ fontSize: '0.7rem', marginLeft: 12 }}>status:</span>
                      {(['open', 'in-progress', 'blocked', 'done'] as const).map(s => (
                        <button key={s} onClick={() => setOverride(i.id, 'status_override', s)}
                          style={{
                            minHeight: 24, padding: '0 6px', fontSize: '0.7rem',
                            background: i.effective_status === s ? 'var(--accent, #5fb0ff)22' : 'transparent',
                          }}>{STATUS_LABEL[s]}</button>
                      ))}
                      <button onClick={() => openInClaude(i.id)}
                        title="Launch a Claude Code session in the relevant repo with this issue as context"
                        style={{
                          marginLeft: 'auto', minHeight: 24, padding: '0 8px', fontSize: '0.7rem',
                          background: 'rgba(90,242,255,0.10)',
                          borderColor: 'rgba(90,242,255,0.4)',
                          color: 'var(--accent)',
                        }}>
                        ▶ open in Claude
                      </button>
                    </div>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
